import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Cpu, ArrowUpRight, ShieldCheck } from 'lucide-react';

const PlayIcon = () => (
  <svg viewBox="0 0 512 512" className="w-5 h-5 flex-shrink-0" aria-hidden="true">
    <path fill="#00d3ff" d="M48 25.6C42 30 38.6 37.4 38.6 47.2v417.6c0 9.8 3.4 17.2 9.4 21.6l233.4-230.4z" />
    <path fill="#00f076" d="M48 25.6l233.4 230.4L344 194.6 90.6 24.4C74.6 13.8 59 15.4 48 25.6z" />
    <path fill="#ffce00" d="M281.4 256L344 317.4l84.6-48.4c19-10.8 19-40.8 0-51.6L344 194.6z" />
    <path fill="#ff3d44" d="M48 486.4c11 10.2 26.6 11.8 42.6 1.2L344 317.4 281.4 256z" />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 384 512" className="w-5 h-5 flex-shrink-0" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

export default function ProjectsSection() {
  const { t } = useTranslation();

  const projects = [
    {
      key: 'mediconnect',
      color: 'text-sky-500',
      span: 'col-span-1 md:col-span-7',
      image: '/images/radioclinic-app.png',
      privacyUrl: '/radioclinic/confidentialite.html',
      stores: {
        playStore: 'https://play.google.com/store/apps/details?id=com.radioclinic.app',
        appStore: '',
      },
    },
    {
      key: 'fintrack',
      color: 'text-violet-500',
      span: 'col-span-1 md:col-span-5',
      image: '/images/kimia-logo.png',
      imgClass: 'object-contain',
      imgStyle: { backgroundColor: '#da7a3d' },
      privacyUrl: '/kimia/confidentialite.html',
      stores: {
        playStore: 'https://play.google.com/store/apps/details?id=app.emergent.kimiaauthdev4f0c5978',
        appStore: '',
      },
    },
    {
      key: 'healthdata',
      color: 'text-emerald-500',
      span: 'col-span-1 md:col-span-5',
      image: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxjb3JvbmF2aXJ1cyUyMGNvdmlkJTIwdmlydXMlMjBjZWxscyUyMG1pY3Jvc2NvcGV8ZW58MHx8fHwxNzgzMDAxNzkwfDA&ixlib=rb-4.1.0&q=85',
    },
    {
      key: 'smartpay',
      color: 'text-sky-500',
      span: 'col-span-1 md:col-span-7',
      image: 'https://images.pexels.com/photos/4841737/pexels-photo-4841737.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <section id="projects" className="relative py-24 md:py-32" data-testid="projects-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-2xl"
        >
          <span className="inline-block font-outfit text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">
            {t('projects.label')}
          </span>
          <h2 className="font-outfit font-medium text-3xl md:text-4xl lg:text-5xl tracking-tight text-zinc-950 mb-5">
            {t('projects.title')}{' '}
            <span className="text-gradient">{t('projects.titleHighlight')}</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-zinc-600 leading-relaxed">
            {t('projects.subtitle')}
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5"
        >
          {projects.map((project) => (
            <motion.div
              key={project.key}
              variants={itemVariants}
              className={`group relative ${project.span} soft-card overflow-hidden`}
              data-testid={`project-${project.key}`}
            >
              {/* Image */}
              <div className={`relative h-44 md:h-56 overflow-hidden ${project.imgBg || ''}`} style={project.imgStyle}>
                <img
                  src={project.image}
                  alt={t(`projects.items.${project.key}.title`)}
                  className={`w-full h-full ${project.imgClass || 'object-cover'} transition-transform duration-500 group-hover:scale-105`}
                  loading="lazy"
                />
                {!project.imgBg && !project.imgStyle && (
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent" />
                )}
                <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-medium bg-white/85 backdrop-blur border border-white text-zinc-700">
                  {t(`projects.items.${project.key}.category`)}
                </span>
              </div>

              {/* Text */}
              <div className="p-6 md:p-7 pt-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-outfit font-medium text-lg md:text-xl text-zinc-900">
                    {t(`projects.items.${project.key}.title`)}
                  </h3>
                  <ArrowUpRight className="w-5 h-5 text-zinc-300 group-hover:text-zinc-900 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <p className="font-manrope text-sm text-zinc-600 leading-relaxed">
                  {t(`projects.items.${project.key}.description`)}
                </p>
                {project.stores && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href={project.stores.playStore}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-950 text-white hover:bg-zinc-800 transition-colors"
                      data-testid={`${project.key}-playstore-btn`}
                    >
                      <PlayIcon />
                      <span className="text-left leading-none">
                        <span className="block text-[8px] uppercase tracking-wider text-zinc-300">Disponible sur</span>
                        <span className="block text-[13px] font-semibold">Google Play</span>
                      </span>
                    </a>
                    <span
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-100 text-zinc-400 border border-zinc-200 cursor-not-allowed"
                      title="Bientôt disponible"
                      data-testid={`${project.key}-appstore-btn`}
                    >
                      <AppleIcon />
                      <span className="text-left leading-none">
                        <span className="block text-[8px] uppercase tracking-wider">Bientôt sur</span>
                        <span className="block text-[13px] font-semibold">App Store</span>
                      </span>
                    </span>
                  </div>
                )}
                {project.privacyUrl && (
                  <a
                    href={project.privacyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-950 transition-colors"
                    data-testid={`project-${project.key}-privacy`}
                  >
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                    {t('projects.privacyLink')}
                    <ArrowUpRight className="w-3 h-3" />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* R&D Section */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-6 p-8 rounded-2xl relative overflow-hidden mesh-soft border border-zinc-200"
          data-testid="projects-rnd"
        >
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-5">
            <div className="w-12 h-12 rounded-xl icon-tile flex items-center justify-center flex-shrink-0">
              <Cpu className="w-6 h-6 text-sky-500" />
            </div>
            <div>
              <h3 className="font-outfit font-medium text-lg md:text-xl mb-2 text-zinc-900">
                {t('projects.rnd.title')}
              </h3>
              <p className="font-manrope text-sm text-zinc-600 leading-relaxed">
                {t('projects.rnd.description')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
